import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.76, 0, 0.24, 1];

export default function MemoRequestModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const firstFieldRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Reset when re-opening
    setState("idle");
    setErrorMsg("");
    // focus name field
    setTimeout(() => firstFieldRef.current?.focus(), 300);
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading") return;
    setState("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/waitlist`, {
        email: email.trim(),
        name: name.trim() || undefined,
        message: message.trim() || undefined,
      });
      setState("success");
    } catch (err) {
      setState("error");
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        status === 422
          ? "Please check your email address."
          : typeof detail === "string"
            ? detail
            : "Something went wrong. Please try again."
      );
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="font-latex fixed inset-0 z-[100] bg-white overflow-y-auto"
          data-testid="memo-modal"
        >
          {/* Close — same spot and style as the header link */}
          <div className="w-full flex justify-end px-8 md:px-12 pt-8 md:pt-10">
            <button
              type="button"
              onClick={onClose}
              className="text-[13px] text-[#0A0A0C]/55 hover:text-[#0A0A0C] transition-colors duration-500"
              data-testid="memo-modal-close"
            >
              close
            </button>
          </div>

          {/* Same type as the page — compact, quiet column */}
          <div className="w-full max-w-[420px] mx-auto px-8 pt-14 md:pt-20 pb-16">
            {state !== "success" ? (
              <>
                <div className="mb-10">
                  <h2 className="font-display text-[18px] md:text-[19px] leading-[1.55] tracking-[-0.005em] font-light text-[#0A0A0C]">
                    Leave your details and we&rsquo;ll send it your way.
                  </h2>
                </div>

                <form onSubmit={submit} noValidate className="space-y-6" data-testid="memo-form">
                  <label className="block">
                    <span className="block text-[13px] text-[#0A0A0C]/55 mb-2">name</span>
                    <input
                      ref={firstFieldRef}
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full bg-transparent border-b border-[#0A0A0C]/15 py-2 text-[15px] text-[#0A0A0C] placeholder-[#0A0A0C]/30 focus:outline-none focus:border-[#0A0A0C] transition-colors"
                      placeholder="Jacques Hadamard"
                      data-testid="memo-input-name"
                    />
                  </label>

                  <label className="block">
                    <span className="block text-[13px] text-[#0A0A0C]/55 mb-2">email</span>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-transparent border-b border-[#0A0A0C]/15 py-2 text-[15px] text-[#0A0A0C] placeholder-[#0A0A0C]/30 focus:outline-none focus:border-[#0A0A0C] transition-colors"
                      placeholder="you@domain.com"
                      data-testid="memo-input-email"
                    />
                  </label>

                  <label className="block">
                    <span className="block text-[13px] text-[#0A0A0C]/55 mb-2">
                      message
                    </span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={3}
                      className="w-full bg-transparent border-b border-[#0A0A0C]/15 py-2 text-[15px] text-[#0A0A0C] placeholder-[#0A0A0C]/30 focus:outline-none focus:border-[#0A0A0C] transition-colors resize-none"
                      placeholder="(optional)"
                      data-testid="memo-input-message"
                    />
                  </label>

                  {state === "error" && (
                    <p className="text-[13px] text-[#B33A3A]" data-testid="memo-error">{errorMsg}</p>
                  )}

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={state === "loading" || !email.trim() || !name.trim()}
                      className="text-[13px] text-[#0A0A0C]/70 underline underline-offset-[6px] decoration-[#0A0A0C]/20 hover:text-[#0A0A0C] hover:decoration-[#0A0A0C]/70 transition-all duration-500 disabled:opacity-40 disabled:hover:text-[#0A0A0C]/70 disabled:hover:decoration-[#0A0A0C]/20"
                      data-testid="memo-submit"
                    >
                      {state === "loading" ? "sending…" : "→ send request"}
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div data-testid="memo-success">
                <h2 className="font-display text-[18px] md:text-[19px] leading-[1.55] tracking-[-0.005em] font-light text-[#0A0A0C]">
                  Request received.
                </h2>
                <p className="mt-4 text-[15px] md:text-[15.5px] leading-[1.65] text-[#0A0A0C]/70">
                  We&rsquo;ll be in touch shortly.
                </p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
